import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  input,
  output,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { CouponService, ExcelService } from '@fitness-force/api';
import { CouponType } from '@fitness-force/enum';
import { AppErrors } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';
import type {
  CouponByMasterId,
  CouponCodes,
  GenerateCouponCodesRequest,
  OfferMaster,
} from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { STAFF_STATE } from '@fitness-force/state';
import { ToastMessageService } from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { PopoverModule } from 'primeng/popover';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TagModule } from 'primeng/tag';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { InputNumberComponent } from '../../input-number/input-number.component';

@Component({
  selector: 'app-single-use-coupon',
  templateUrl: './single-use-coupon.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollPanelModule,
    ButtonModule,
    InputNumberModule,
    TagModule,
    PopoverModule,
    ErrorMsgComponent,
    InputNumberComponent,
    TranslateModule,
    AsyncPipe,
  ],
})
export class SingleUseCouponComponent implements OnInit {
  private coupnService = inject(CouponService);
  formBuilder = inject(UntypedFormBuilder);
  translate = inject(TranslateService);
  private toastMessageService = inject(ToastMessageService);
  private excelService = inject(ExcelService);
  private store = inject(Store);

  readonly couponMaster = input<OfferMaster>();
  readonly visible = input<boolean>(false);
  readonly closePopup = output<any>({ alias: 'closePopup' });

  coupons$ = new BehaviorSubject<CouponByMasterId[]>([]);
  formGroup: UntypedFormGroup;
  generateCouponsModal = new BehaviorSubject(false);
  couponArray: string[] = [];
  loadingBtn: boolean = false;

  getAccessedStaffList$ = new BehaviorSubject<any[] | any>([]);

  ngOnInit(): void {
    this.getCouponsByMasterId();
    this.GetStaffs();
  }

  getCouponsByMasterId() {
    const couponMaster = this.couponMaster();
    if (!couponMaster) {
      return;
    }
    this.coupnService
      .getCouponsByMasterid(couponMaster.tenant_id, couponMaster.id)
      .subscribe({
        next: (response: any) => {
          this.coupons$.next(response.items as CouponByMasterId[]);
          // visibility controlled by parent component
        },
        error: (error: any) => {
          this.closePopup.emit(true);
        },
      });
  }

  openAddCoupon() {
    this.formGroup = this.formBuilder.group({
      max_redepmtion_count: new UntypedFormControl(null, [Validators.required]),
    });
    this.generateCouponsModal.next(true);
  }

  GenerateMoreCoupons() {
    const couponMaster = this.couponMaster();
    if (!couponMaster) {
      return;
    }
    if (this.formGroup.valid) {
      this.loadingBtn = true;
      let payload = {} as GenerateCouponCodesRequest;
      payload.coupon_type = couponMaster.coupon_type.toString();
      payload.coupon_codes = [];
      let coupons = this.generateCoupons();
      coupons.forEach((o) => {
        let coupon: CouponCodes = {
          coupon_code: '',
          max_redepmtion_count: 0,
        };
        coupon.coupon_code = o;
        coupon.max_redepmtion_count = this.maxRedemptionCountControl.value;
        payload.coupon_codes.push(coupon);
      });
      this.coupnService
        .GenerateMoreCoupons(couponMaster.tenant_id, couponMaster.id, payload)
        .subscribe({
          next: (response: any) => {
            this.loadingBtn = false;
            this.generateCouponsModal.next(false);
            this.translate
              .get('ADMIN.COUPON.ERRORS.CRETE_MORE_COUPON')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            let couponList: CouponByMasterId[] = [];
            coupons.forEach((o) => {
              let coupons: CouponByMasterId = {} as CouponByMasterId;
              coupons.coupon_code = o;
              coupons.coupon_max_redepmtion_count =
                this.maxRedemptionCountControl.value;
              couponList.push(coupons);
            });

            this.coupons$.next([...this.coupons$.getValue(), ...couponList]);
          },
          error: (error: AppErrors) => {
            this.loadingBtn = false;
            this.handleToastError(error);
          },
        });
    } else {
    }
  }

  closeAddCouponPopup() {
    this.formGroup.reset();
    this.generateCouponsModal.next(false);
  }

  CloseCouponPopup() {
    this.closePopup.emit(true);
    //this.couponsModal.next(false);
  }

  handleToastError(error: any) {
    this.toastMessageService.showError(error);
  }

  generateCoupons() {
    this.couponArray = [];
    for (let i = 0; i < this.maxRedemptionCountControl.value; i++) {
      let coupon = this.randomString(4) + this.mathRandom();
      this.couponArray.push(coupon);
    }
    return this.couponArray;
  }

  randomString(len: any, an?: any) {
    an = an && an.toLowerCase();
    var str = '',
      i = 0,
      min = an == 'a' ? 10 : 0,
      max = an == 'n' ? 10 : 62;
    for (; i++ < len; ) {
      var r = (Math.random() * (max - min) + min) << 0;
      str += String.fromCharCode((r += r > 9 ? (r < 36 ? 55 : 61) : 48));
    }
    return str.toUpperCase();
  }

  mathRandom(): any {
    let a = Math.floor(Math.random() * (9999 - 1000 + 1)) + 1000;
    return a.toString().length == 4 ? a : this.mathRandom();
  }

  MarkActiveInactiveCouponLink(
    couponCodeLink: CouponByMasterId,
    status: boolean,
  ) {
    let service: Observable<any>;
    if (status) {
      service = this.coupnService.MarkCouponCodeLinkActive(
        couponCodeLink.tenant_id,
        couponCodeLink.offer_master_id,
        couponCodeLink.id,
      );
    } else {
      service = this.coupnService.MarkCouponCodeLinkInActive(
        couponCodeLink.tenant_id,
        couponCodeLink.offer_master_id,
        couponCodeLink.id,
      );
    }
    service.subscribe({
      next: (response: any) => {
        this.translate
          .get(
            status
              ? 'ADMIN.COUPON.ERRORS.MARK_AS_ACTIVE'
              : 'ADMIN.COUPON.ERRORS.MARK_AS_INACTIVE',
          )
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
        this.getCouponsByMasterId();
      },
      error: (error: any) => {
        this.handleToastError(error);
      },
    });
  }

  async DownloadCSV() {
    let couponData: any = [];
    this.coupons$.getValue().forEach((o) => {
      let couponCode = {
        CouponName: '',
        Status: '',
        TotalCount: 0,
        CreatedBy: '',
        CreatedDate: '',
      };
      couponCode.CouponName = o.coupon_code;
      couponCode.TotalCount = o.coupon_max_redepmtion_count;
      couponCode.Status =
        o.coupon_current_redepmtion_count > 0 ? 'USED' : 'UNUSED';
      couponCode.CreatedBy = this.getStaffName(o.created_by);
      couponCode.CreatedDate = o.created_date.toString();
      couponData.push(couponCode);
    });
    this.excelService.exportAsExcelFile(couponData, 'singleuse_coupon', 'csv');
  }

  GetStaffs() {
    this.store.select(STAFF_STATE).subscribe((data: any) => {
      if (data && data.staffList && data.staffList.length > 0) {
        this.getAccessedStaffList$.next(
          data.staffList.filter((o: any) => {
            return String(o.status).toLowerCase() == 'active';
          }),
        );
      }
    });
  }

  getStaffName(staffId: number) {
    return this.getAccessedStaffList$
      .getValue()
      .find((o: { id: number }) => o.id == staffId).name;
  }

  get maxRedemptionCountControl(): AbstractControl {
    return this.formGroup.controls.max_redepmtion_count;
  }
}
